import { memoContextKey } from "./memo-node-id";
import type {
  MemoPanelIntent,
  UserMemoContext,
} from "./workspace-side-panel-mode";

export type MemoPanelMemo = {
  __typename: "UserMemo" | "SystemMemo";
  id: string;
  label: string;
  body: string | null;
  color?: string | null;
  memoType: "USER" | "SYSTEM";
  dateMode: "NONE" | "DAY" | "RANGE";
  startDate?: string | null;
  endDate?: string | null;
  publicationScope: "VIEW" | "PROJECT" | "FEATURE" | "WORKSPACE";
  featureKind?:
    | "SEARCH_ANALYTICS"
    | "USER_ANALYTICS"
    | "INDEX_WORKER"
    | "DASHBOARD"
    | null;
  sourceLabel?: string | null;
  sourceUrl?: string | null;
  links?: readonly {
    url: string;
    label: string;
    kind: "OFFICIAL" | "RELATED";
    primary: boolean;
  }[];
  systemLabels?: readonly {
    key?: string | null;
    name?: string | null;
    color?: string | null;
    localizedTexts?: readonly {
      locale?: string | null;
      name?: string | null;
    }[];
  }[];
  originTargetKind?: UserMemoContext["targetKind"] | null;
  originTargetID?: string | null;
  provider?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string; picture?: string | null } | null;
  updatedBy?: { id: string; name: string; picture?: string | null } | null;
  deletedBy?: { id: string; name: string; picture?: string | null } | null;
  canEdit?: boolean;
  canDelete?: boolean;
  canRestore?: boolean;
};

export type UserMemoFeatureKind = NonNullable<MemoPanelMemo["featureKind"]>;

export type PanelFilter = {
  query: string;
  authorUserID: string;
  publicationScope: "ALL" | "VIEW" | "PROJECT" | "FEATURE" | "WORKSPACE";
  memoType: "ALL" | "USER" | "SYSTEM";
  sourceFilters: MemoSourceFilterValue[];
  dateStart: string;
  dateEnd: string;
  deleted: "ACTIVE" | "DELETED" | "ALL";
};

export const USER_SOURCE_FILTER_VALUE = "USER";
export const SYSTEM_MEMO_SOURCE_FILTER_VALUE = "SYSTEM_MEMO";
const SYSTEM_MEMO_LABEL_SOURCE_FILTER_PREFIX = "SYSTEM_LABEL:";

export const MEMO_SOURCE_FILTER_VALUES = [
  USER_SOURCE_FILTER_VALUE,
  SYSTEM_MEMO_SOURCE_FILTER_VALUE,
] as const;

type BaseMemoSourceFilterValue = (typeof MEMO_SOURCE_FILTER_VALUES)[number];

export type MemoSourceFilterValue =
  | BaseMemoSourceFilterValue
  | `${typeof SYSTEM_MEMO_LABEL_SOURCE_FILTER_PREFIX}${string}`;

export type SystemMemoLabelFilterOption = {
  key: string;
  name: string;
  color?: string | null;
  localizedTexts?: readonly {
    locale?: string | null;
    name?: string | null;
  }[];
};

export function memoSourceFilterValueForSystemLabelKey(
  key: string,
): MemoSourceFilterValue {
  return `${SYSTEM_MEMO_LABEL_SOURCE_FILTER_PREFIX}${key}`;
}

export function memoSourceFilterValuesForSystemLabels(
  systemMemoLabels: readonly SystemMemoLabelFilterOption[],
): MemoSourceFilterValue[] {
  return [
    ...MEMO_SOURCE_FILTER_VALUES,
    ...systemMemoLabels.map((label) =>
      memoSourceFilterValueForSystemLabelKey(label.key),
    ),
  ];
}

export type PanelDateFilterRange = {
  startDate: string;
  endDate: string;
};

export type MemoFormState = {
  title: string;
  body: string;
  color: string;
  dateMode: "NONE" | "DAY" | "RANGE";
  startDate: string;
  endDate: string;
  publicationScope: "VIEW" | "PROJECT" | "FEATURE" | "WORKSPACE";
  createTargetKey?: string;
};

export const MEMO_TITLE_MAX_LENGTH = 60;
export const MEMO_BODY_MAX_LENGTH = 500;
export const MEMO_SEARCH_DEBOUNCE_MS = 250;

export const DEFAULT_FILTER: PanelFilter = {
  query: "",
  authorUserID: "ALL",
  publicationScope: "ALL",
  memoType: "ALL",
  sourceFilters: [...MEMO_SOURCE_FILTER_VALUES],
  dateStart: "",
  dateEnd: "",
  deleted: "ACTIVE",
};

export function defaultFilterForSystemMemoLabels(
  systemMemoLabels: readonly SystemMemoLabelFilterOption[],
): PanelFilter {
  return {
    ...DEFAULT_FILTER,
    sourceFilters: memoSourceFilterValuesForSystemLabels(systemMemoLabels),
  };
}

/**
 * side panel intent から新規 memo の初期 form state を作ります。
 * chart 起点の作成だけ初期日付を単日として反映し、panel 起点では date-less のまま開始します。
 */
export function formStateFromIntent(
  intent: MemoPanelIntent,
  defaultColor: string,
): MemoFormState {
  return {
    title: "",
    body: "",
    color: defaultColor,
    dateMode: intent.state === "create" && intent.initialDate ? "DAY" : "NONE",
    startDate: intent.state === "create" ? (intent.initialDate ?? "") : "",
    endDate: "",
    publicationScope: "VIEW",
    createTargetKey:
      intent.state === "create" ? memoContextKey(intent.context) : undefined,
  };
}

/**
 * 保存済み memo を編集 form state へ写します。
 * null 許容 field は UI が扱いやすい空文字へ寄せ、draft と server data を混ぜないようにします。
 */
export function formStateFromMemo(
  memo: MemoPanelMemo,
  defaultColor: string,
  targetKey?: string,
): MemoFormState {
  return {
    title: memo.label,
    body: memo.body ?? "",
    color: memo.color ?? defaultColor,
    dateMode: memo.dateMode,
    startDate: memo.startDate ?? "",
    endDate: memo.endDate ?? "",
    publicationScope: memo.publicationScope,
    createTargetKey: targetKey,
  };
}

/**
 * user memo context から GraphQL memo context input を作ります。
 * client では ID と feature 種別だけを渡し、権限や派生 key は backend に委譲します。
 */
export function memoContextFromUserMemoContext(context: UserMemoContext) {
  return {
    workspaceID: context.workspaceId,
    featureKind: context.featureKind,
    projectID: context.projectId || null,
    targetKind: context.targetKind,
    targetID: context.targetId,
  };
}

/**
 * panel intent の route context から GraphQL memo context input を作ります。
 * create form で target を選び直す場合は、選択後 context を直接渡す helper を使います。
 */
export function memoContextInput(intent: MemoPanelIntent) {
  return memoContextFromUserMemoContext(intent.context);
}
